import React from 'react';
import {
  Row, Col, Button, Form,
} from 'react-bootstrap';
import { useForm, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import api from 'api';
import useFeedback from 'hooks/useFeedback';
import useUser from 'hooks/useUser';

type BenchmarkType = Record<string, unknown> | null;

type AboutValues = {
  name: string | null;
  hospitalName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  city: string | null;
  region: string | null;
  zipPostalCode: string | null;
  location: string | null;
  country: string | null;
  phoneNumber: string | null;
  tollFreeNumber: string | null;
  faxNumber: string | null;
  email: string | null;
  website: string | null;
};

const emptyToNull = (v: unknown) => {
  if (typeof v === 'string') {
    const trimmed = v.trim();
    return trimmed === '' ? null : trimmed;
  }
  return v;
};

const schema: yup.ObjectSchema<AboutValues> = yup
  .object({
    name: yup.string().transform(emptyToNull).nullable().defined(),
    hospitalName: yup.string().transform(emptyToNull).nullable().defined(),

    addressLine1: yup.string().transform(emptyToNull).nullable().defined(),
    addressLine2: yup.string().transform(emptyToNull).nullable().defined(),
    addressLine3: yup.string().transform(emptyToNull).nullable().defined(),
    city: yup.string().transform(emptyToNull).nullable().defined(),
    region: yup.string().transform(emptyToNull).nullable().defined(),
    zipPostalCode: yup.string().transform(emptyToNull).nullable().defined(),
    location: yup.string().transform(emptyToNull).nullable().defined(),
    country: yup.string().transform(emptyToNull).nullable().defined(),
    phoneNumber: yup.string().transform(emptyToNull).nullable().defined(),
    tollFreeNumber: yup.string().transform(emptyToNull).nullable().defined(),
    faxNumber: yup.string().transform(emptyToNull).nullable().defined(),
    email: yup
      .string()
      .transform(emptyToNull)
      .email('Invalid email')
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email')
      .nullable()
      .defined(),
    website: yup
      .string()
      .transform(emptyToNull)
      .url('Invalid URL')
      .nullable()
      .defined(),
  })
  .required();

type Props = {
  id: string;
  canEdit: boolean;
  canEditCenterIdentity: boolean;
  values: Readonly<Partial<AboutValues>>;
  benchmark: BenchmarkType;
  onSaved?: () => void;
};

type FieldProps = {
  label: string;
  name: keyof AboutValues;
  type?: string;
  form: Pick<UseFormReturn<AboutValues>, 'register' | 'formState'>;
  editing: boolean;
  values: AboutValues;
  disabled?: boolean;
  autoComplete?: string;
};

const ReadOnlyAddressBlock: React.FC<{ values: AboutValues }> = ({ values }) => {
  const lines = [values.addressLine1, values.addressLine2, values.addressLine3].filter(Boolean);
  if (!lines.length) return null;

  return (
    <div className="mb-3">
      <h6 className="fw-bold mb-1" style={{ minHeight: '2rem' }}>
        Address
      </h6>
      <p className="mb-0">
        {lines.map((line) => (
          <React.Fragment key={`address${line}` as string}>
            {line}
            {line !== lines[lines.length - 1] ? <br /> : null}
          </React.Fragment>
        ))}
      </p>
    </div>
  );
};

const Field = React.memo((props: FieldProps) => {
  const {
    label,
    name,
    type = 'text',
    form,
    editing,
    values,
    disabled,
    autoComplete,
  } = props;
  const error = form.formState.errors[name]?.message as string | undefined;

  if (!editing) {
    const val = values[name];
    return (
      <div className="mb-3">
        <h6 className="fw-bold mb-1" style={{ minHeight: '2rem' }}>
          {label}
        </h6>
        <p className="mb-0">
          {val ?? <span className="text-muted fst-italic">n/a</span>}
        </p>
      </div>
    );
  }

  return (
    <Form.Group className="mb-3">
      <Form.Label className="fw-bold">{label}</Form.Label>
      <Form.Control
        type={type}
        {...form.register(name)}
        isInvalid={!!error}
        placeholder=""
        disabled={disabled}
        autoComplete={autoComplete}
      />
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </Form.Group>
  );
});

const normalizeDefaults = (v: Partial<AboutValues>): AboutValues => ({
  name: v.name ?? null,
  hospitalName: v.hospitalName ?? null,
  addressLine1: v.addressLine1 ?? null,
  addressLine2: v.addressLine2 ?? null,
  addressLine3: v.addressLine3 ?? null,
  city: v.city ?? null,
  region: v.region ?? null,
  zipPostalCode: v.zipPostalCode ?? null,
  location: v.location ?? null,
  country: v.country ?? null,
  phoneNumber: v.phoneNumber ?? null,
  tollFreeNumber: v.tollFreeNumber ?? null,
  faxNumber: v.faxNumber ?? null,
  email: v.email ?? null,
  website: v.website ?? null,
});

const AboutTab: React.FC<Props> = ({
  id,
  canEdit,
  canEditCenterIdentity,
  values: incoming,
  benchmark,
  onSaved,
}) => {
  const {
    isBcAdmin,
  } = useUser();
  const [editing, setEditing] = React.useState(false);
  const { setError } = useFeedback?.() ?? {
    setError: () => {},
  };
  const defaults = React.useMemo(() => normalizeDefaults(incoming), [incoming]);

  const form = useForm<AboutValues>({
    defaultValues: defaults,
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const { isSubmitting, isDirty } = form.formState;

  const hasAddress = defaults?.addressLine1 || defaults?.addressLine2 || defaults?.addressLine3;

  React.useEffect(() => {
    if (!editing) form.reset(normalizeDefaults(incoming));
  }, [incoming, editing, form]);

  const onSubmit = React.useCallback(
    async (data: AboutValues) => {
      const payload = {
        ...data,
        benchmark: benchmark ?? null,
        name: canEditCenterIdentity ? data.name : defaults.name,
        hospitalName: canEditCenterIdentity ? data.hospitalName : defaults.hospitalName,
      };
      try {
        if (isBcAdmin) {
          await api.updateCurrentBurnCenter({ ...payload, id });
        } else {
          await api.updateBurnCenterById(id, payload);
        }

        setEditing(false);
        onSaved?.();
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        setEditing(false);
        setError?.(detail || 'Failed to update burn center.');
      }
    },
    [id, benchmark, canEditCenterIdentity,
      defaults.hospitalName, defaults.name, isBcAdmin, onSaved, setError],
  );

  const handleCancel = React.useCallback(() => {
    form.reset(defaults);
    setEditing(false);
  }, [defaults, form]);

  return (
    <div>
      <div className="d-flex justify-content-end align-items-end mb-3">
        {canEdit && !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        {canEdit && editing && (
          <div>
            <Button
              variant="secondary"
              className="me-2"
              type="submit"
              form="about-form"
              disabled={isSubmitting || !isDirty}
            >
              {isSubmitting ? 'Updating…' : 'Update'}
            </Button>
            <Button
              variant="outline-secondary"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      <Form id="about-form" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        {!editing ? (
          <Row className="g-3 align-items-start">
            <Col xs={12} md={4}>
              {hasAddress && <ReadOnlyAddressBlock values={defaults} />}
              <Field label="City" name="city" form={form} editing={false} values={defaults} />
              <Field label="Region" name="region" form={form} editing={false} values={defaults} />
              <Field label="Postal Code" name="zipPostalCode" form={form} editing={false} values={defaults} />
              <Field label="State or Province" name="location" form={form} editing={false} values={defaults} />
              <Field label="Country" name="country" form={form} editing={false} values={defaults} />
            </Col>

            <Col xs={12} md={4}>
              <Field label="Phone number" name="phoneNumber" form={form} editing={false} values={defaults} />
              <Field label="Toll free number" name="tollFreeNumber" form={form} editing={false} values={defaults} />
              <Field label="Fax number" name="faxNumber" form={form} editing={false} values={defaults} />
            </Col>

            <Col xs={12} md={4}>
              <Field label="Website" name="website" form={form} editing={false} values={defaults} />
              <Field label="Email" name="email" type="email" form={form} editing={false} values={defaults} />
            </Col>
          </Row>
        ) : (
          <>
            {canEditCenterIdentity && (
              <Row className="g-3 align-items-start mb-0">
                <Col xs={12} md={4}>
                  <Field
                    label="Burn Center Name"
                    name="name"
                    form={form}
                    editing={editing}
                    values={defaults}
                    disabled={isSubmitting}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Field
                    label="Hospital Name"
                    name="hospitalName"
                    form={form}
                    editing={editing}
                    values={defaults}
                    disabled={isSubmitting}
                  />
                </Col>
              </Row>
            )}

            <Row className="g-3 align-items-start">
              <Col xs={12} md={4}>
                <Field
                  label="Address one"
                  name="addressLine1"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="address-line1"
                />
                <Field
                  label="Address two"
                  name="addressLine2"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="address-line2"
                />
                <Field
                  label="Address three"
                  name="addressLine3"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="address-line3"
                />
                <Field
                  label="City"
                  name="city"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="address-level2"
                />
                <Field
                  label="Region"
                  name="region"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="address-level1"
                />
                <Field
                  label="Postal Code"
                  name="zipPostalCode"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="postal-code"
                />
                <Field
                  label="State or Province"
                  name="location"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="location"
                />
                <Field
                  label="Country"
                  name="country"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="country"
                />
              </Col>

              <Col xs={12} md={4}>
                <Field
                  label="Phone number"
                  name="phoneNumber"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="tel"
                />
                <Field
                  label="Toll free number"
                  name="tollFreeNumber"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                />
                <Field
                  label="Fax number"
                  name="faxNumber"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                />
              </Col>

              <Col xs={12} md={4}>
                <Field
                  label="Website"
                  name="website"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="url"
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  form={form}
                  editing={editing}
                  values={defaults}
                  disabled={isSubmitting}
                  autoComplete="email"
                />
              </Col>
            </Row>
          </>
        )}
      </Form>
    </div>
  );
};

export default AboutTab;
